import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { echarts, type ECOption, type EChartsType } from '../lib/echarts';
import { CHART_DRAW_IN_MS, prefersReducedMotion } from '../ui/motion';
import styles from './EChart.module.css';

export interface EChartProps {
  option: ECOption;
  height: number;
  label: string;
  group?: string;
  // Read at mount only; set false when a remount should not replay the draw-in.
  drawIn?: boolean;
  onReady?: (chart: EChartsType) => void;
}

function chartMotion(option: ECOption, drawIn: boolean): ECOption {
  return drawIn
    ? { ...option, animation: true, animationDuration: CHART_DRAW_IN_MS, animationEasing: 'cubicOut' }
    : { ...option, animation: false };
}

// Canvas text is painted once; repaint when web fonts arrive (jsdom has no document.fonts).
function repaintAfterFonts(notBefore: number, repaint: () => void): () => void {
  const fontSet = document.fonts as FontFaceSet | undefined;
  if (fontSet === undefined) return () => undefined;
  let live = true;
  let timer: number | undefined;
  const schedule = (): void => {
    if (!live) return;
    window.clearTimeout(timer);
    // A notMerge repaint during the draw-in would cut it short, so wait for it to finish.
    timer = window.setTimeout(repaint, Math.max(0, notBefore - Date.now()));
  };
  // Fonts already loaded: the first paint used them, nothing to repaint.
  if (fontSet.status !== 'loaded') void fontSet.ready.then(schedule);
  fontSet.addEventListener('loadingdone', schedule);
  return () => {
    live = false;
    window.clearTimeout(timer);
    fontSet.removeEventListener('loadingdone', schedule);
  };
}

export function EChart({ option, height, label, group, drawIn = true, onReady }: EChartProps) {
  const [mayDrawIn] = useState(drawIn);
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [chart, setChart] = useState<EChartsType | null>(null);
  const drawn = useRef(false);
  const onReadyRef = useRef(onReady);
  useLayoutEffect(() => {
    onReadyRef.current = onReady;
  });

  useEffect(() => {
    if (node === null) return undefined;
    const instance = echarts.init(node, undefined, { renderer: 'canvas' });
    if (group !== undefined) {
      instance.group = group;
      echarts.connect(group);
    }
    // ECharts redraws on every resize() with animation off, which cuts the draw-in short. The observer's
    // first callback reports the size init already measured, and often lands after the draw-in starts.
    const observer = new ResizeObserver(() => {
      if (node.clientWidth !== instance.getWidth() || node.clientHeight !== instance.getHeight()) instance.resize();
    });
    observer.observe(node);
    drawn.current = false;
    setChart(instance);
    return () => {
      observer.disconnect();
      instance.dispose();
    };
  }, [node, group]);

  useEffect(() => {
    if (chart === null) return undefined;
    const animate = mayDrawIn && !drawn.current && !prefersReducedMotion();
    drawn.current = true;
    chart.setOption(chartMotion(option, animate), { notMerge: true });
    onReadyRef.current?.(chart);
    const settled = Date.now() + (animate ? CHART_DRAW_IN_MS : 0);
    return repaintAfterFonts(settled, () => chart.setOption(chartMotion(option, false), { notMerge: true }));
  }, [chart, option]);

  return <div ref={setNode} className={styles.chart} style={{ height }} role="img" aria-label={label} />;
}
